// @ts-nocheck
// Polls a claim-import extraction's status (PRD Phase 3), mirroring
// useLMNLetterPolling: dual cancel guard (cancelledRef + per-run `active`),
// clears the interval on terminal status AND on unmount, keyed by
// extractionId + restartKey.
import { useCallback, useEffect, useRef, useState } from 'react';

import { getClaimImportStatus } from '../api/claimImportApi';

export const TERMINAL = new Set(['success', 'needs_review', 'failed']);
const POLL_MS = 3000;

export function useClaimImportPolling(extractionId, restartKey = 0) {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const cancelledRef = useRef(false);
  const intervalRef = useRef(null);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setStatus((prev) => (prev && !TERMINAL.has(prev) ? 'cancelled' : prev));
  }, []);

  useEffect(() => {
    if (!extractionId) {
      setStatus(null);
      return undefined;
    }
    cancelledRef.current = false;
    setError(null);
    let active = true;

    async function poll() {
      if (cancelledRef.current || !active) return;
      try {
        const fresh = await getClaimImportStatus(extractionId);
        if (cancelledRef.current || !active) return;
        setStatus(fresh.ocr_status);
        setData(fresh);
        if (fresh.ocr_status === 'failed') {
          setError(fresh.error_message || 'Extraction failed.');
        } else {
          setError(null);
        }
        if (TERMINAL.has(fresh.ocr_status) && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } catch (_e) {
        // Swallow transient errors — the next tick retries.
      }
    }

    poll();
    intervalRef.current = setInterval(poll, POLL_MS);
    return () => {
      active = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [extractionId, restartKey]);

  return { status, data, error, cancel };
}
